/**
 * ThemeModeProvider — клей между Jotai-атомами и DOM.
 *  1. Слушает `prefers-color-scheme` и держит `systemDarkAtom` в актуальном.
 *  2. Пишет на `<html>`: `data-theme`, `data-accent` + плоский набор
 *     CSS-переменных (base tokens + accent override).
 *
 * Wraps app внутри `<App/>` — провайдер ничего не рендерит, кроме
 * children. Тема меняется без перезагрузки страницы.
 */

import type {ReactNode} from 'react';
import {useEffect, useMemo} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';

import {accentChoiceAtom, accentVarsFor} from '@atoms/accent';
import {effectiveThemeAtom, systemDarkAtom} from '@atoms/theme';
import {cssVarsFor} from '@styles/tokens';

type ThemeModeProviderProps = {
  children: ReactNode;
};

export const ThemeModeProvider = ({children}: ThemeModeProviderProps) => {
  const setSystemDark = useSetAtom(systemDarkAtom);
  const mode = useAtomValue(effectiveThemeAtom);
  const accent = useAtomValue(accentChoiceAtom);

  // matchMedia → systemDarkAtom.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [setSystemDark]);

  // База + accent (accent перебивает brand'овые).
  const vars = useMemo(
    () => ({...cssVarsFor(mode), ...accentVarsFor(accent, mode)}),
    [mode, accent],
  );

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    root.dataset.accent = accent;
    for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
  }, [mode, accent, vars]);

  return <>{children}</>;
};
